import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ProtectedRoute({ children, allowedRoles }) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    null // null = checking, true = authenticated, false = not authenticated
  );
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const applyRoleCheck = (u) => {
      if (!allowedRoles || allowedRoles.length === 0) {
        setIsAuthorized(true);
        return;
      }
      if (u && allowedRoles.includes(u.role)) {
        setIsAuthorized(true);
      } else if (u && u.is_master_editor === true && allowedRoles.includes('super_admin')) {
        // iter 217: grant SA-only routes to flagged Master Editors
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
      }
    };

    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      applyRoleCheck(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true
        });

        setUser(response.data);
        setIsAuthenticated(true);
        applyRoleCheck(response.data);
      } catch (error) {
        setIsAuthenticated(false);
        
        // Save where the user was trying to go so AuthCallback can redirect
        // them back to the same place after Google sign-in.
        const currentPath = location.pathname + (location.search || '');
        // Redirect to appropriate login page based on path
        if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/headoffice') || location.pathname.startsWith('/inventory')) {
          localStorage.setItem('intended_redirect_path', currentPath);
          navigate('/admin/login');
        } else if (location.pathname.startsWith('/teacher')) {
          localStorage.setItem('intended_redirect_path', currentPath);
          navigate('/teacher/login');
        } else if (location.pathname.startsWith('/tests')) {
          localStorage.setItem('intended_redirect_path', currentPath);
          navigate('/teacher/login');
        } else if (location.pathname.startsWith('/hrms/hr')) {
          navigate('/hrms/login?portal=hr');
        } else if (location.pathname.startsWith('/hrms/employee')) {
          navigate('/hrms/login?portal=employee');
        } else if (location.pathname.startsWith('/hrms/recruiter')) {
          navigate('/hrms/login?portal=recruiter');
        } else if (location.pathname.startsWith('/student')) {
          // Student visiting /student/* → save intent + brand /login as Student Portal
          localStorage.setItem('intended_redirect_path', currentPath);
          localStorage.setItem('intended_portal', 'student');
          navigate('/login');
        } else if (location.pathname.startsWith('/parent')) {
          localStorage.setItem('intended_redirect_path', currentPath);
          localStorage.setItem('intended_portal', 'parent');
          navigate('/login');
        } else if (location.pathname.startsWith('/partner')) {
          localStorage.setItem('intended_redirect_path', currentPath);
          localStorage.setItem('intended_portal', 'partner');
          navigate('/partner/login');
        } else if (location.pathname.startsWith('/leads') || location.pathname.startsWith('/presales')) {
          // iter 284: dedicated pre-sales portal login
          localStorage.setItem('intended_redirect_path', currentPath);
          localStorage.setItem('intended_portal', 'pre_sales');
          navigate('/presales');
        } else {
          // Default = parent portal (dashboard etc.)
          localStorage.setItem('intended_portal', 'parent');
          navigate('/login');
        }
      }
    };

    checkAuth();
  }, [navigate, location.state, location.pathname, allowedRoles]);

  if (isAuthenticated === null || isAuthorized === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthorized === false) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950 p-6">
        <div className="max-w-md text-center bg-slate-900/60 border border-red-500/40 rounded-lg p-8" data-testid="access-denied">
          <div className="text-red-400 text-5xl mb-4">⛔</div>
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400 text-sm mb-4">
            This page is restricted. Your current role (<span className="text-slate-200 font-mono">{user?.role || 'unknown'}</span>) does not have permission to view it.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;
